import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { API, useAuthedApi } from "./api";
import { playChime, unlockAudio } from "./notify";

const RtCtx = createContext({ counts: { notifications: 0, chats: 0 }, refresh: () => {}, subscribe: () => () => {}, live: false });
const viewing = (path) => document.visibilityState === "visible" && window.location.pathname === path;

export function RealtimeProvider({ children }) {
  const { isSignedIn, getToken } = useAuth();
  const api = useAuthedApi();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ notifications: 0, chats: 0 });
  const [live, setLive] = useState(false);
  const subs = useRef(new Set());

  const refresh = useCallback(() => {
    if (isSignedIn) api("get", "/me/counts").then(setCounts).catch(() => {});
  }, [isSignedIn, api]);

  useEffect(() => {
    // Browsers only allow sound after a user gesture; unlock on the first tap/click.
    const u = () => { unlockAudio(); window.removeEventListener("pointerdown", u); };
    window.addEventListener("pointerdown", u);
    const onSw = (e) => e.data?.type === "open" && navigate(new URL(e.data.url).pathname);
    navigator.serviceWorker?.addEventListener("message", onSw);
    return () => { window.removeEventListener("pointerdown", u); navigator.serviceWorker?.removeEventListener("message", onSw); };
  }, [navigate]);

  const onEvent = (ev) => {
    if (ev.type === "notification") {
      refresh();
      const n = ev.notification;
      if (viewing(n.link)) return;
      if (["message", "offer", "offer_status"].includes(n.kind)) playChime();
      toast(n.title, { description: n.body, ...(n.link ? { action: { label: n.kind === "message" ? "Open chat" : "Open", onClick: () => navigate(n.link) } } : {}) });
    }
    if (ev.type === "message") refresh();
  };
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!isSignedIn) { setCounts({ notifications: 0, chats: 0 }); return; }
    refresh();
    let ws, stop = false, retry;
    const connect = async () => {
      const token = await getToken();
      if (stop || !token) return;
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}`);
      ws.onopen = () => setLive(true);
      ws.onmessage = (e) => {
        const ev = JSON.parse(e.data);
        subs.current.forEach((fn) => fn(ev));
        handler.current(ev);
      };
      ws.onclose = () => { setLive(false); if (!stop) retry = setTimeout(connect, 4000); };
    };
    connect();
    const poll = setInterval(refresh, 30000);
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25000);
    return () => { stop = true; clearTimeout(retry); clearInterval(poll); clearInterval(ping); ws?.close(); };
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((fn) => { subs.current.add(fn); return () => subs.current.delete(fn); }, []);
  return <RtCtx.Provider value={{ counts, refresh, subscribe, live }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);
